import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Avatar,
  AvatarGroup,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  alpha // Added alpha import
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ThumbDown as ThumbDownIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { workOrdersApi, sectionsApi } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import WorkOrderDetailsModal from './components/WorkOrderDetailsModal';
import WorkOrderCancelConfirmationDialog from './components/WorkOrderCancelConfirmationDialog'; // Renamed import
import WorkOrderTimeProgressBar from './components/WorkOrderTimeProgressBar';
import EditWorkOrderDialog from './components/EditWorkOrderDialog'; // Import the new dialog
import { useTheme } from '@mui/material'; // Added useTheme hook

const WorkOrderListPage = () => {
  const theme = useTheme(); // Added useTheme hook
  const { canAccess, getUserOrganization } = useAuth();
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Statistics
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    assigned: 0,
    in_progress: 0,
    completed: 0,
    completed_temporary: 0,
    rejected_count: 0,
    cancelled_count: 0,
    overdue: 0
  });
  
  // Menu and dialog state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false); // State for the edit dialog

  // Cancel Confirmation Dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedWorkOrderForCancel, setSelectedWorkOrderForCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportStatus, setReportStatus] = useState('all');
  const [reportSection, setReportSection] = useState('all');
  const [sections, setSections] = useState([]);

  const fetchSections = useCallback(async () => {
    try {
      const response = await sectionsApi.getAll();
      setSections(response.data || []);
    } catch (error) {
      console.error('Failed to fetch sections:', error);
    }
  }, []);

  useEffect(() => {
    if (reportDialogOpen) {
      fetchSections();
    }
  }, [reportDialogOpen, fetchSections]);

  const handleOpenReportDialog = () => {
    setReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    try {
      const params = {};
      if (reportStatus !== 'all') params.status = reportStatus;
      if (reportSection !== 'all') params.section_id = reportSection;

      const response = await workOrdersApi.getWorkOrdersReport(params);
      const data = response.data.data;

      if (data && data.length > 0) {
        const headers = Object.keys(data[0]);
        const csvContent = [
          headers.join(','),
          ...data.map(row =>
            headers.map(header => JSON.stringify(row[header])).join(',')
          ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', 'work_order_report.csv');
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        handleCloseReportDialog();
      } else {
        alert('No data to export for the selected criteria.');
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      setError('Failed to generate report. Please try again.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const fetchWorkOrders = useCallback(async () => {
    if (!canAccess(['manager'])) return;
    
    try {
      setLoading(true);
      setError('');
      
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_by: 'created_at',
        sort_order: 'DESC'
      };

      // Add filters
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (priorityFilter !== 'all') {
        params.priority = priorityFilter;
      }
      if (assignmentFilter !== 'all') {
        params.assignment_type = assignmentFilter;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await workOrdersApi.getWorkOrders(params);
      setWorkOrders(response.data.data || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch action log:', error);
      setError('Failed to load action log. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, priorityFilter, assignmentFilter, searchTerm, canAccess]);

  const fetchStatistics = useCallback(async () => {
    if (!canAccess(['manager'])) return;
    
    try {
      const response = await workOrdersApi.getStatistics();
      setStatistics(response.data.data || {});
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  }, [canAccess]);

  useEffect(() => {
    if (canAccess(['manager'])) {
      fetchWorkOrders();
      fetchStatistics();
    }
  }, [fetchWorkOrders, fetchStatistics, canAccess]);

  // Check access after all hooks are defined
  if (!canAccess(['manager'])) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Only managers can access action log management.
        </Alert>
      </Box>
    );
  }

  const userOrganization = getUserOrganization();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedWorkOrder(null);
  };

  // Cancel handlers
  const handleCancelClick = (workOrder) => {
    console.log('[WorkOrderListPage] Cancel clicked for:', workOrder);
    setSelectedWorkOrderForCancel(workOrder);
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setSelectedWorkOrderForCancel(null);
  };

  const handleEdit = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setSelectedWorkOrder(null);
  };

  const handleConfirmCancel = async () => {
    if (!selectedWorkOrderForCancel) return;

    setCancelling(true);
    try {
      const response = await workOrdersApi.deleteWorkOrder(selectedWorkOrderForCancel.id);

      if (response.status === 200) {
        // Assuming a success message is handled by a global snackbar or similar
        // For now, just log and refresh
        console.log('Work order cancelled successfully:', response.data);
        handleCloseCancelDialog();
        fetchWorkOrders(); // Refresh the list
        fetchStatistics(); // Refresh statistics
      } else {
        throw new Error('Cancel operation failed');
      }
    } catch (error) {
      console.error('Error cancelling work order:', error);
      // Handle error display, e.g., with a snackbar
      // setError(`Failed to cancel work order: ${error.response?.data?.error || error.message}`);
    } finally {
      setCancelling(false);
    }
  };

  const handleRefresh = () => {
    fetchWorkOrders();
    fetchStatistics();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'assigned': return 'info';
      case 'in_progress': return 'primary';
      case 'overdue': return 'error';
      case 'completed_temporary': return 'secondary';
      case 'completed': return 'success';
      case 'rejected': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getAssignmentDisplay = (workOrder) => {
    if (workOrder.assignment_type === 'internal') {
      if (workOrder.assignees && workOrder.assignees.length > 1) {
        return (
          <Tooltip title={workOrder.assignees.map(a => a.full_name).join(', ')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" color="primary" />
              <Typography variant="body2">
                Internal Users ({workOrder.assignees.length})
              </Typography>
            </Box>
          </Tooltip>
        );
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" color="primary" />
          <Typography variant="body2">
            {workOrder.assignees?.[0]?.full_name || 'Internal User'}
          </Typography>
        </Box>
      );
    } else {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon fontSize="small" color="secondary" />
          <Typography variant="body2">
            {workOrder.assigned_company_name || 'External Contractor'}
          </Typography>
        </Box>
      );
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading action log..." />;
  }

  return (
    <Box sx={{ padding: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Action Log
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive action log management and assignment system
        </Typography>
      </Box>

      {/* Controls and Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={isGeneratingReport ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleOpenReportDialog}
            disabled={isGeneratingReport}
          >
            Generate Report
          </Button>
        </Box>
      </Box>

      <Dialog open={reportDialogOpen} onClose={handleCloseReportDialog}>
        <DialogTitle>Generate Report</DialogTitle>
        <DialogContent>
          <Box sx={{ minWidth: 400, mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={reportStatus}
                label="Status"
                onChange={(e) => setReportStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="completed_temporary">Completed Temporary</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Section</InputLabel>
              <Select
                value={reportSection}
                label="Section"
                onChange={(e) => setReportSection(e.target.value)}
              >
                <MenuItem value="all">All Sections</MenuItem>
                {sections.map((section) => (
                  <MenuItem key={section.id} value={section.id}>
                    {section.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportDialog}>Cancel</Button>
          <Button onClick={handleGenerateReport} variant="contained" disabled={isGeneratingReport}>
            {isGeneratingReport ? 'Generating...' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Statistics Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(8, 1fr)',
            xl: 'repeat(8, 1fr)'
          },
          gap: 2,
          mb: 3
        }}
      >
        {/* Total Action Log */}
        <Card sx={{ boxShadow: theme.shadows[2], borderRadius: '12px', background: 'linear-gradient(135deg, #5b86e5, #36d1dc)', color: 'white', minHeight: '120px' }}>
          <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', height: '100%' }}>
            <AssignmentIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>{statistics.total_work_orders || 0}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Total Action Log</Typography>
          </CardContent>
        </Card>

        {/* Assigned */}
        <Card sx={{ boxShadow: theme.shadows[2], borderRadius: '12px', background: 'linear-gradient(135deg, #4fc3f7, #2196f3)', color: 'white', minHeight: '120px' }}>
          <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', height: '100%' }}>
            <PersonIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>{statistics.assigned_count || 0}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Assigned</Typography>
          </CardContent>
        </Card>

        {/* In Progress */}
        <Card sx={{ boxShadow: theme.shadows[2], borderRadius: '12px', background: 'linear-gradient(135deg, #66bb6a, #43a047)', color: 'white', minHeight: '120px' }}>
          <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', height: '100%' }}>
            <TrendingUpIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>{statistics.in_progress_count || 0}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>In Progress</Typography>
          </CardContent>
        </Card>

        {/* Overdue */}
        <Card sx={{ boxShadow: theme.shadows[2], borderRadius: '12px', background: 'linear-gradient(135deg, #ef5350, #c62828)', color: 'white', minHeight: '120px' }}>
          <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', height: '100%' }}>
            <WarningIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>{statistics.overdue_count || 0}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Overdue</Typography>
          </CardContent>
        </Card>

        {/* Completed Temporary */}
        <Card sx={{ boxShadow: theme.shadows[2], borderRadius: '12px', background: 'linear-gradient(135deg, #ffb74d, #ff9800)', color: 'white', minHeight: '120px' }}>
          <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', height: '100%' }}>
            <ScheduleIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>{statistics.completed_temporary_count || 0}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Completed Temporary</Typography>
          </CardContent>
        </Card>

        {/* Completed */}
        <Card sx={{ boxShadow: theme.shadows[2], borderRadius: '12px', background: 'linear-gradient(135deg, #81c784, #4caf50)', color: 'white', minHeight: '120px' }}>
          <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', height: '100%' }}>
            <CheckCircleIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>{statistics.completed_count || 0}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Completed</Typography>
          </CardContent>
        </Card>

        {/* Rejected */}
        <Card sx={{ boxShadow: theme.shadows[2], borderRadius: '12px', background: 'linear-gradient(135deg, #e57373, #f44336)', color: 'white', minHeight: '120px' }}>
          <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', height: '100%' }}>
            <ThumbDownIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>{statistics.rejected_count || 0}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Rejected</Typography>
          </CardContent>
        </Card>

        {/* Cancelled */}
        <Card sx={{ boxShadow: theme.shadows[2], borderRadius: '12px', background: 'linear-gradient(135deg, #90a4ae, #607d8b)', color: 'white', minHeight: '120px' }}>
          <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', height: '100%' }}>
            <CancelIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>{statistics.cancelled_count || 0}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Cancelled</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search action log..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="assigned">Assigned</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="completed_temporary">Completed Temporary</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <MenuItem value="all">All Priority</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Assignment</InputLabel>
                <Select
                  value={assignmentFilter}
                  label="Assignment"
                  onChange={(e) => setAssignmentFilter(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="internal">Internal</MenuItem>
                  <MenuItem value="external">External</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setPriorityFilter('all');
                  setAssignmentFilter('all');
                  setPage(0);
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Work Orders Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Action Log</TableCell>
                <TableCell>Defect</TableCell>
                <TableCell>Assignment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No action log found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                workOrders.map((workOrder) => (
                  <TableRow
                    key={workOrder.id}
                    hover
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {workOrder.work_order_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {workOrder.title}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        D-{workOrder.envis_defect_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getAssignmentDisplay(workOrder)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={workOrder.status}
                        color={getStatusColor(workOrder.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={workOrder.priority}
                        color={getPriorityColor(workOrder.priority)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(workOrder.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <WorkOrderTimeProgressBar
                        createdAt={workOrder.created_at}
                        dueDate={workOrder.due_date}
                        status={workOrder.status}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(workOrder)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Action Log">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(workOrder)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel Action Log">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click
                            handleCancelClick(workOrder);
                          }}
                          sx={{
                            color: theme.palette.error.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.error.main, 0.1),
                            }
                          }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      </Card>

      {/* Reusable Work Order Details Modal */}
      {selectedWorkOrder && (
        <WorkOrderDetailsModal
          open={detailsDialogOpen}
          onClose={handleCloseDetails}
          workOrder={selectedWorkOrder}
        />
      )}

      {/* Edit Work Order Dialog */}
      {selectedWorkOrder && (
        <EditWorkOrderDialog
          open={editDialogOpen}
          onClose={handleCloseEdit}
          workOrder={selectedWorkOrder}
          onUpdate={() => {
            fetchWorkOrders();
            fetchStatistics();
          }}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      <WorkOrderCancelConfirmationDialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        workOrder={selectedWorkOrderForCancel}
        onConfirmCancel={handleConfirmCancel}
        cancelling={cancelling}
      />
    </Box>
  );
};

export default WorkOrderListPage;